import * as React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useOrgLegal } from "@/api/legal";
import { AcceptOrgTermsButton, OrgTermsDocuments } from "@/features/legal/orgTermsShared";
import { legalDay } from "@/features/legal/orgTermsText";

const dismissKey = (slug: string, terms: number, dpa: number) =>
  `rw.orgTermsPrompt.${slug}.${terms}.${dpa}`;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function dismissedToday(key: string): boolean {
  try {
    return localStorage.getItem(key) === today();
  } catch {
    return false;
  }
}

/** An org admin's prompt while the org is ``pending`` (spec §9.1): what
 *  changed, the deadline, the new versions, "Accept for <org>" and
 *  "Later". "Later" hides it for the rest of the day, per version. */
export function OrgTermsPrompt({
  slug,
  onOpenChange,
}: {
  slug: string;
  /** Whether the dialog is open, or may still open while the org's
   *  versions load: the legal notices wait for it to close. */
  onOpenChange?: (open: boolean) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const legal = useOrgLegal(slug);
  const [closed, setClosed] = React.useState(false);
  const data = legal.data;
  const key = data ? dismissKey(slug, data.latest.terms, data.latest.dpa) : null;
  const open = legal.isPending
    ? !closed
    : !!data && !!key && data.state === "pending" && !closed && !dismissedToday(key);
  React.useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);
  if (!open || !data || !key) return null;
  const later = () => {
    try {
      localStorage.setItem(key, today());
    } catch {
      /* private mode: it shows again on the next load */
    }
    setClosed(true);
  };
  const date = legalDay(data.deadline_date);
  return (
    <Dialog open onClose={later} aria-labelledby="org-terms-prompt-title" maxWidth="sm" fullWidth>
      <DialogTitle id="org-terms-prompt-title">{t("legal.org.prompt.title")}</DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2 }}>
          <Typography>
            {date ? t("legal.org.prompt.body", { date }) : t("legal.org.prompt.body_no_date")}
          </Typography>
          <OrgTermsDocuments legal={data} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, alignItems: "flex-end" }}>
        <Button onClick={later}>{t("legal.org.prompt.later")}</Button>
        <AcceptOrgTermsButton slug={slug} legal={data} onAccepted={() => setClosed(true)} />
      </DialogActions>
    </Dialog>
  );
}
